import React, { Component } from "react";
import client from "@culturehq/client";
import { Form, ModalDialog, NumberField, SelectField, StringField, SubmitButton } from "@culturehq/components";

import { useProfile } from "app/context";
import { t, Trans } from "app/utils";
import getBadgeTypes from "constants/getBadgeTypes";

import ErrorMessage from "components/common/ErrorMessage";
import ChqModalDialog from "components/common/ChqModalDialog";

const getDefaultPointsValue = (isEvent, profile) => {
  const BadgeTypes = getBadgeTypes(profile);

  const badgeType = isEvent ? "event_survey" : "widget_survey";
  return BadgeTypes.find(({ key }) => key === badgeType).points;
};

const makePointsValidator = min => points => {
  const normal = Number(points);

  if (!normal || normal < min) {
    return t(`Score must be greater than or equal to the default value (${min})`);
  }
  return null;
};

const SurveyResultsField = ({ event }) => {
  if (event && event.id) {
    const hostOnlyOptions = [
      { value: false, label: t("Yes – share with admins") },
      { value: true, label: t("No – only visible to me") }
    ];

    return (
      <SelectField name="hostOnlySurvey" options={hostOnlyOptions}>
        <Trans>Share Survey Results</Trans>
      </SelectField>
    );
  }

  return (
    <SelectField
      ariaLabel={t("Can users view results after taking survey?")}
      name="viewableResults"
      options={[
        { value: true, label: t("Yes - show results to users") },
        { value: false, label: t("No - does not show results to users") }
      ]}
      required
    >
      <Trans>Users can view results after taking survey</Trans>
    </SelectField>
  );
};

const getSubmitButtonText = survey => {
  const resting = (survey && survey.id) ? t("Save") : t("Continue and Add Items");
  return submitting => (submitting ? t("Saving...") : resting);
};

class SurveyEditModalContext extends Component {
  state = { error: null, open: false };

  componentDidMount() {
    this.componentIsMounted = true;
  }

  componentWillUnmount() {
    this.componentIsMounted = false;
  }

  handleOpen = () => {
    this.setState({ open: true });
  };

  handleClose = () => {
    this.setState({ open: false });
  };

  handleSubmit = async values => {
    const { onEditSuccess, survey, event, onEventUpdate } = this.props;

    const options = { ...values };
    const surveyId = survey && survey.id;
    const eventId = event && event.id;

    this.setState({ error: false });

    if (eventId) {
      options.eventId = eventId;
    }

    try {
      let response;

      if (surveyId) {
        response = await client.updateSurvey({ ...options, surveyId });
      } else {
        response = await client.createSurvey(options);
      }

      if (eventId && onEventUpdate) {
        const { event: eventResponse } = await client.getEvent({ eventId });
        onEventUpdate(eventResponse);
      }

      if (onEditSuccess) {
        onEditSuccess(response.survey);
      }

      if (this.componentIsMounted) {
        this.setState({ open: false });
      }
    } catch (error) {
      if (this.componentIsMounted) {
        this.setState({ error });
      }
    }
  };

  render() {
    const { children, event, profile, survey } = this.props;
    const { error, open } = this.state;

    if (!profile.organization.gamificationEnabled && survey && survey.id) {
      return null;
    }

    const isEvent = event && event.id;
    const defaultPoints = getDefaultPointsValue(isEvent, profile);

    const initialValues = {
      title: isEvent ? `${event.name} Post-Event Survey` : "",
      points: defaultPoints,
      hostOnlySurvey: event && event.hostOnlySurvey,
      ...(survey || {})
    };

    return (
      <>
        {children(this.handleOpen)}
        {open && (
          <ChqModalDialog onClose={this.handleClose} width="narrow">
            <ModalDialog.Heading onClose={this.handleClose}>
              {(survey && survey.id) ? t("Edit Details") : t("Create New Survey")}
            </ModalDialog.Heading>
            <ModalDialog.Body>
              {error && <ErrorMessage error={error} />}
              <Form onSubmit={this.handleSubmit} initialValues={initialValues}>
                {!event && (
                  <StringField name="title" required>
                    <Trans>Survey Title</Trans>
                  </StringField>
                )}
                <SurveyResultsField event={event} />
                {profile.organizationAdmin && (
                  <NumberField name="points" validator={makePointsValidator(defaultPoints)}>
                    <Trans>Score</Trans>
                  </NumberField>
                )}
                <SubmitButton primary>
                  {getSubmitButtonText(survey)}
                </SubmitButton>
              </Form>
            </ModalDialog.Body>
          </ChqModalDialog>
        )}
      </>
    );
  }
}

const SurveyEditModal = props => {
  const { profile } = useProfile();

  return <SurveyEditModalContext {...props} profile={profile} />;
};

export default SurveyEditModal;
